import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Trophy, Copy, Check, Link2, TrendingUp, Wallet, CreditCard,
  ShieldCheck, Clock, ChevronRight, DollarSign, BadgeCheck, AlertCircle,
} from "lucide-react";
import api, { apiError } from "@/lib/api";

const fmtCr = (c) => (c || 0).toLocaleString();
const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const STATUS_STYLE = {
  pending: "text-race-volt border-race-volt/40",
  approved: "text-sky-400 border-sky-400/40",
  paid: "text-green-400 border-green-400/40",
  reversed: "text-race-sub border-race-line",
};

export default function RacingTeam() {
  const [data, setData] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wbusy, setWbusy] = useState(false);
  const [params] = useSearchParams();

  const load = () => api.get("/affiliate/me").then((r) => setData(r.data)).catch(() => {});
  const loadWallet = () => api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
  useEffect(() => { load(); loadWallet(); }, []);

  // After returning from Stripe Connect onboarding, refresh payout status.
  useEffect(() => {
    if (params.get("connect")) {
      api.get("/affiliate/connect/status").then(() => load()).catch(() => {});
      if (params.get("connect") === "return") toast.success("Payout setup updated");
    }
  }, [params]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.referral_link);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Could not copy"); }
  };

  const setupPayouts = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/affiliate/connect/onboard");
      window.location.href = r.url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const startKyc = async () => {
    setWbusy(true);
    try {
      const { data: r } = await api.post("/kyc/session", { origin_url: window.location.origin });
      if (r.already_verified) { toast.success("Already verified"); loadWallet(); setWbusy(false); return; }
      window.location.href = r.url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail?.message || e.response?.data?.detail));
      setWbusy(false);
    }
  };

  const withdraw = async () => {
    setWbusy(true);
    try {
      const { data: r } = await api.post("/withdrawals");
      toast.success(`Withdrawal of ${usd(r.amount)} sent to your payout account.`);
      await Promise.all([load(), loadWallet()]);
    } catch (e) {
      const d = e.response?.data?.detail;
      const code = d?.code;
      if (code === "kyc_required") { toast.message("Verify your identity to withdraw."); return startKyc(); }
      if (code === "connect_required") { toast.message("Set up your payout account first."); return setupPayouts(); }
      toast.error(apiError(d?.message || d));
    } finally { setWbusy(false); }
  };

  if (!data) return <div className="p-10 text-race-sub" data-testid="rt-loading">Loading your Racing Team…</div>;

  const { tier, next_tier, qualified_count, total_team, earnings, team, commissions, connect } = data;
  const progressPct = next_tier
    ? Math.min(100, Math.round((qualified_count / next_tier.at) * 100))
    : 100;

  return (
    <div className="animate-slide-up space-y-8" data-testid="racing-team-page">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
          <Users className="text-race-red" /> MY RACING TEAM
        </h1>
        <p className="text-race-sub text-sm mt-2 max-w-2xl">
          Recruit racers with your link. When a teammate wins <span className="text-race-txt">1st place</span> in an
          eligible contest, you earn a commission - funded by IBRA, never taken from the winner's prize.
        </p>
      </div>

      {/* Referral link */}
      <div className="bg-race-surface border border-race-line p-5" data-testid="rt-referral">
        <div className="overline mb-2 flex items-center gap-2"><Link2 size={13} /> Your Referral Link</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input readOnly value={data.referral_link} data-testid="rt-referral-input"
            className="flex-1 bg-race-bg border border-race-line px-3 py-2.5 rounded-sm font-mono text-sm text-race-txt outline-none" />
          <button onClick={copyLink} data-testid="rt-copy-link"
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm bg-race-red hover:bg-race-redhover text-race-bg font-mono text-xs uppercase tracking-wide transition-colors">
            {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="mt-2 text-race-muted text-xs font-mono">Code: {data.affiliate_code}</div>
      </div>

      {/* Cash Out - unified wallet for winnings + commissions, KYC-gated */}
      {wallet && (
        <div className="bg-race-surface border border-race-line p-5" data-testid="rt-wallet">
          <div className="flex items-center justify-between mb-4">
            <div className="overline flex items-center gap-2"><Wallet size={13} /> Cash Out</div>
            {wallet.kyc_verified ? (
              <span className="flex items-center gap-1 text-green-400 text-xs font-mono" data-testid="rt-kyc-verified">
                <BadgeCheck size={14} /> ID Verified
              </span>
            ) : (
              <span className="flex items-center gap-1 text-race-sub text-xs font-mono" data-testid="rt-kyc-unverified">
                <ShieldCheck size={14} /> Verify at cash-out
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Money label="Winnings" value={wallet.winnings_available} testid="rt-wallet-winnings" />
            <Money label="Commissions" value={wallet.commissions_available} testid="rt-wallet-commissions" />
            <Money label="Pending review" value={wallet.commissions_pending} muted testid="rt-wallet-pending" />
            <Money label="Available now" value={wallet.total_available} accent testid="rt-wallet-total" />
          </div>

          {wallet.total_available > 0 ? (
            !wallet.kyc_verified ? (
              <button onClick={startKyc} disabled={wbusy} data-testid="rt-verify-identity"
                className="w-full flex items-center justify-center gap-2 bg-race-volt text-race-bg font-heading text-xl tracking-wide py-3 rounded-sm hover:opacity-90 disabled:opacity-50 transition-opacity">
                <ShieldCheck size={18} /> {wbusy ? "Opening…" : "Verify identity to withdraw"}
              </button>
            ) : !wallet.connect.payouts_enabled ? (
              <button onClick={setupPayouts} disabled={busy} data-testid="rt-wallet-setup-payouts"
                className="w-full flex items-center justify-center gap-2 bg-race-txt/10 border border-race-line hover:border-race-red text-race-txt font-mono text-xs uppercase tracking-wide py-3 rounded-sm disabled:opacity-50 transition-colors">
                <CreditCard size={16} /> {busy ? "Opening…" : wallet.connect.has_account ? "Finish payout setup to withdraw" : "Set up payout account to withdraw"}
              </button>
            ) : (
              <button onClick={withdraw} disabled={wbusy} data-testid="rt-withdraw"
                className="w-full flex items-center justify-center gap-2 bg-race-red hover:bg-race-redhover text-race-bg font-heading text-xl tracking-wide py-3 rounded-sm disabled:opacity-50 transition-colors">
                <DollarSign size={18} /> {wbusy ? "Processing…" : `Withdraw ${usd(wallet.total_available)}`}
              </button>
            )
          ) : (
            <div className="text-center text-race-sub text-sm py-2" data-testid="rt-wallet-empty">
              No funds available yet. Win a contest or earn a commission to build your balance.
            </div>
          )}

          <p className="text-race-muted text-[11px] mt-3 flex items-start gap-1.5">
            <AlertCircle size={12} className="mt-0.5 shrink-0" />
            A one-time identity check (government ID + selfie) is required before your first
            cash-out. Play and refer freely - verification only happens when you withdraw.
          </p>

          {wallet.withdrawals?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-race-line" data-testid="rt-withdrawals">
              <div className="overline text-race-sub mb-2">Withdrawal History</div>
              <div className="space-y-2">
                {wallet.withdrawals.map((w) => (
                  <div key={w.id} className="flex items-center justify-between text-xs bg-race-bg border border-race-line px-3 py-2 rounded-sm" data-testid={`rt-withdrawal-${w.id}`}>
                    <span className="text-race-sub font-mono">{new Date(w.created_at).toLocaleDateString()}</span>
                    <span className="text-green-400 font-mono">{usd(w.amount)} paid</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard Icon={ShieldCheck} label="Current Tier" value={tier.name}
          sub={`${Math.round(tier.rate * 100)}% commission`} testid="rt-tier" accent />
        <StatCard Icon={Users} label="Qualified Racers" value={qualified_count}
          sub={`${total_team} total recruited`} testid="rt-qualified" />
        <StatCard Icon={Wallet} label="Lifetime Earnings" value={fmtCr(earnings.lifetime)}
          sub={usd(earnings.lifetime)} testid="rt-lifetime" />
        <StatCard Icon={Clock} label="Pending" value={fmtCr(earnings.pending)}
          sub={usd(earnings.pending)} testid="rt-pending" />
      </div>

      {/* Tier progress */}
      <div className="bg-race-surface border border-race-line p-5" data-testid="rt-progress">
        <div className="flex items-center justify-between mb-3">
          <div className="overline flex items-center gap-2"><TrendingUp size={13} /> Tier Progress</div>
          {next_tier ? (
            <div className="text-xs text-race-sub">
              <span className="text-race-txt">{next_tier.needed}</span> more qualified racers →{" "}
              <span className="text-race-volt">{next_tier.name} ({Math.round(next_tier.rate * 100)}%)</span>
            </div>
          ) : <div className="text-xs text-green-400">Top tier reached 🏁</div>}
        </div>
        <div className="h-3 bg-race-bg border border-race-line rounded-full overflow-hidden">
          <div className="h-full bg-race-red transition-all" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="flex justify-between mt-3 gap-2">
          {data.tiers.map((t) => (
            <div key={t.name} className={`flex-1 text-center rounded-sm border py-2 ${qualified_count >= t.min ? "border-race-red/40 bg-race-elevated" : "border-race-line"}`}>
              <div className="font-heading text-lg">{Math.round(t.rate * 100)}%</div>
              <div className="overline text-[9px]">{t.name}</div>
              <div className="text-race-muted text-[10px]">{t.max ? `${t.min}–${t.max}` : `${t.min}+`}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Payout setup */}
      <div className="bg-race-surface border border-race-line p-5" data-testid="rt-payouts">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="overline mb-1 flex items-center gap-2"><CreditCard size={13} /> Payout Account</div>
            <p className="text-sm text-race-sub max-w-md">
              {connect.payouts_enabled
                ? "Your Stripe payout account is active. Approved commissions can be released to you."
                : connect.has_account
                  ? "Finish your Stripe onboarding to receive payouts."
                  : "Connect a bank account via Stripe to receive your commission payouts."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {connect.payouts_enabled
              ? <span className="flex items-center gap-1 text-green-400 text-sm font-mono" data-testid="rt-payouts-active"><Check size={16} /> Active</span>
              : (
                <button onClick={setupPayouts} disabled={busy} data-testid="rt-setup-payouts"
                  className="px-4 py-2.5 rounded-sm bg-race-txt/10 border border-race-line hover:border-race-red text-race-txt font-mono text-xs uppercase tracking-wide transition-colors disabled:opacity-50">
                  {busy ? "Opening…" : connect.has_account ? "Continue Setup" : "Set Up Payouts"}
                </button>
              )}
          </div>
        </div>
      </div>

      {/* Two columns: team directory + win history */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-race-surface border border-race-line p-5" data-testid="rt-team-directory">
          <h2 className="font-heading text-2xl tracking-tight mb-4 flex items-center gap-2">
            <Users size={20} className="text-race-red" /> Team Directory
          </h2>
          {team.length === 0 ? (
            <p className="text-race-sub text-sm">No recruits yet. Share your link to build your team.</p>
          ) : (
            <div className="space-y-2">
              {team.map((m, i) => (
                <div key={i} className="flex items-center justify-between bg-race-bg border border-race-line px-3 py-2.5 rounded-sm" data-testid={`rt-team-member-${i}`}>
                  <span className="font-mono text-sm text-race-txt">{m.name}</span>
                  {m.qualified
                    ? <span className="flex items-center gap-1 text-green-400 text-xs"><ShieldCheck size={13} /> Qualified</span>
                    : <span className="text-race-muted text-xs">Unlicensed</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-race-surface border border-race-line p-5" data-testid="rt-win-history">
          <h2 className="font-heading text-2xl tracking-tight mb-4 flex items-center gap-2">
            <Trophy size={20} className="text-race-volt" /> Commission History
          </h2>
          {commissions.length === 0 ? (
            <p className="text-race-sub text-sm">No commissions yet. They appear when a teammate wins 1st place.</p>
          ) : (
            <div className="space-y-2">
              {commissions.map((c) => (
                <div key={c.id} className="bg-race-bg border border-race-line px-3 py-2.5 rounded-sm" data-testid={`rt-commission-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-race-txt truncate">{c.contest_name || "Contest"}</span>
                    <span className={`text-[10px] uppercase font-mono border rounded-full px-2 py-0.5 ${STATUS_STYLE[c.status] || ""}`}>{c.status}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1 text-xs text-race-sub">
                    <span>{c.referred_name} won • {Math.round((c.rate || 0) * 100)}% • {c.tier_name}</span>
                    <span className="text-race-volt font-mono">+{fmtCr(c.commission_amount)} ({usd(c.commission_amount)})</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ Icon, label, value, sub, testid, accent }) {
  return (
    <div className={`bg-race-surface border p-5 ${accent ? "border-race-red/40" : "border-race-line"}`} data-testid={testid}>
      <div className="overline mb-2 flex items-center gap-2"><Icon size={13} /> {label}</div>
      <div className="font-heading text-3xl tracking-tight">{value}</div>
      <div className="text-race-sub text-xs mt-1">{sub}</div>
    </div>
  );
}

function Money({ label, value, testid, accent, muted }) {
  return (
    <div className={`bg-race-bg border rounded-sm p-3 ${accent ? "border-race-volt/40" : "border-race-line"}`} data-testid={testid}>
      <div className="overline text-[9px] text-race-sub mb-1">{label}</div>
      <div className={`font-heading text-xl tracking-tight ${accent ? "text-race-volt" : muted ? "text-race-sub" : "text-race-txt"}`}>
        {usd(value)}
      </div>
    </div>
  );
}